import React from 'react';
import styled from 'styled-components';

const WelcomeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 56px);
`;

const Hero = styled.section`
  background-color: #00205B;
  color: #ffffff;
  padding: 96px 24px 80px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  max-width: 640px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-size: 1.05rem;
  font-weight: 400;
  margin: 0 0 36px;
  line-height: 1.7;
  color: #ffffffbb;
`;

const HeroCTA = styled.button`
  background: #ffffff;
  color: #00205B;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: 'Roboto', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f4ff;
    transform: translateY(-1px);
  }
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 48px;
  margin-top: 48px;
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #ffffff99;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const Main = styled.section`
  flex: 1;
  background-color: #f8fafc;
  padding: 56px 24px;
`;

const SectionLabel = styled.p`
  text-align: center;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 8px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 40px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 840px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

const FeatureCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 28px 24px;

  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
  }

  p {
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
  }
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 16px;
`;

const Footer = styled.footer`
  text-align: center;
  padding: 24px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  color: #94a3b8;
  font-size: 0.8rem;

  p { margin: 0; }
`;

interface WelcomeScreenProps {
  onNavigate?: (view: string) => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onNavigate }) => {
  return (
    <WelcomeWrapper>
      <Hero>
        <HeroContent>
          <HeroTitle>Practice with real past exams</HeroTitle>
          <HeroSubtitle>
            Access previous Rice exams, track your progress, and find out exactly what to study. Built by students, for students.
          </HeroSubtitle>
          <HeroCTA onClick={() => onNavigate?.('courses')}>
            Browse Courses
          </HeroCTA>
          <StatsRow>
            <Stat>
              <StatValue>170+</StatValue>
              <StatLabel>Questions</StatLabel>
            </Stat>
            <Stat>
              <StatValue>9</StatValue>
              <StatLabel>Topics</StatLabel>
            </Stat>
            <Stat>
              <StatValue>7</StatValue>
              <StatLabel>Exams</StatLabel>
            </Stat>
          </StatsRow>
        </HeroContent>
      </Hero>

      <Main>
        <SectionLabel>How it works</SectionLabel>
        <SectionTitle>Everything you need to prepare</SectionTitle>
        <Grid>
          <FeatureCard>
            <FeatureIcon>&#9201;</FeatureIcon>
            <h3>Timed Practice</h3>
            <p>Simulate real exam conditions with a 1-hour timer. Submit when you're ready and review your results.</p>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>&#128241;</FeatureIcon>
            <h3>Practice Anywhere</h3>
            <p>Works on your phone, tablet, or laptop. Study on the go between classes or wherever you are.</p>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>&#127919;</FeatureIcon>
            <h3>Study by Topic</h3>
            <p>Build custom practice sets from specific topics like derivatives, integrals, or optimization.</p>
          </FeatureCard>
        </Grid>
      </Main>

      <Footer>
        <p>Rice Exams &middot; Built for Rice University students</p>
      </Footer>
    </WelcomeWrapper>
  );
};

export default WelcomeScreen;
